import React from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Mail, ChevronLeft } from "lucide-react";

/**
 * Shared layout for /privacy and /terms.
 *
 * Renders a hero band with the document title and "Last updated" stamp,
 * an in-page Table of Contents on the left (desktop) and a single column
 * of long-form content on the right. Markdown-like markup is achieved
 * through a small set of helper components exported below.
 */
export default function LegalLayout({ title, subtitle, updatedAt, toc, children }) {
  return (
    <div className="marketing-font min-h-screen bg-[#F4F8FF] text-slate-900">
      <Navbar />

      {/* Page hero */}
      <header
        className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-10"
        data-testid="legal-hero"
      >
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-[#2563EB]"
          data-testid="legal-back-home"
        >
          <ChevronLeft className="w-4 h-4" /> Back to home
        </Link>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mt-4">{title}</h1>
        {subtitle && <p className="mt-3 text-lg text-slate-500 max-w-2xl">{subtitle}</p>}
        <p className="mt-4 text-xs font-medium uppercase tracking-wider text-slate-400">
          Last updated · {updatedAt}
        </p>
      </header>

      {/* Body */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="grid lg:grid-cols-[14rem_1fr] gap-10">
          {/* Sidebar — TOC */}
          {toc && (
            <aside className="hidden lg:block">
              <nav className="sticky top-24 space-y-1.5" aria-label="On-page navigation">
                <p className="text-[10px] font-heading font-bold uppercase tracking-wider text-slate-400 mb-3">
                  On this page
                </p>
                {toc.map((t) => (
                  <a
                    key={t.id}
                    href={`#${t.id}`}
                    className="block text-sm text-slate-600 hover:text-[#2563EB] py-1.5 border-l-2 border-transparent hover:border-[#2563EB] pl-3 transition-colors"
                  >
                    {t.label}
                  </a>
                ))}
              </nav>
            </aside>
          )}

          {/* Content column */}
          <article
            className="bg-white rounded-3xl border border-slate-200/70 shadow-floating p-7 sm:p-10 leading-relaxed text-[15px] text-slate-700"
            data-testid="legal-content"
          >
            {children}

            {/* Contact bottom card */}
            <div className="mt-12 pt-8 border-t border-slate-200/70 flex items-start gap-3">
              <span className="w-10 h-10 rounded-2xl bg-[#DBEAFE] flex items-center justify-center shrink-0">
                <Mail className="w-5 h-5 text-[#2563EB]" />
              </span>
              <div>
                <p className="font-heading font-bold">Questions about this document?</p>
                <p className="text-sm text-slate-500">
                  Email us at{" "}
                  <a href="mailto:legal@schoolboss.ph" className="text-[#2563EB] font-semibold hover:underline">
                    legal@schoolboss.ph
                  </a>{" "}
                  and we'll respond within 5 business days.
                </p>
              </div>
            </div>
          </article>
        </div>
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tiny markup helpers — keep the content pages readable
// ---------------------------------------------------------------------------
export const H2 = ({ id, children }) => (
  <h2
    id={id}
    className="font-heading text-2xl font-bold tracking-tight mt-10 mb-3 scroll-mt-24 text-slate-900"
  >
    {children}
  </h2>
);

export const H3 = ({ children }) => (
  <h3 className="font-heading text-lg font-bold mt-6 mb-2 text-slate-900">{children}</h3>
);

export const P = ({ children }) => <p className="mb-4">{children}</p>;

export const UL = ({ children }) => (
  <ul className="list-disc pl-5 mb-4 space-y-1.5 marker:text-slate-400">{children}</ul>
);

export const Note = ({ children }) => (
  <div className="my-5 bg-[#EFF6FF] border-l-4 border-[#2563EB] rounded-r-xl px-4 py-3 text-[14px] text-slate-700">
    {children}
  </div>
);
